// Top-level form wrapper. Per spec/tech-arch.md §7.
//
// **Top-level union → last variant inline.** Per D-033, several historical
// V1 inputsSchemas were `z.union` of two object arms with paired-optional
// CTA. The wrapper treats `root.kind === 'union'` (last-arm = object) the
// same as `root.kind === 'object'`, picking the last variant's children;
// safeParse picks the matching arm at runtime, and `flattenIssues`
// surfaces per-field errors at their natural paths. After P-037.16
// D-116, no remaining Block inputsSchema is a top-level z.union, but
// the wrapper retains the union-arm fallback for forward compat.
//
// **`arrayOverrides?` cross-schema dispatch (P-032).** Path-keyed
// overrides let the parent screen force fixed-slot rendering on an
// array field whose schema doesn't carry `.length(N)` at compile time.
// Used by the Email editor to surface big-3 mode (`event-card-group`'s
// `inputs.events` renders as 3 fixed slots labeled "Meeting 1/2/3"
// when the sibling `options.style === 'big-3'`). Per the Tech Arch §7
// design surface + P-027 forward note + the P-032 handoff §E
// recommendation (option (a)). The override is consulted only at
// `<ListField>` (P-027); other field kinds ignore it. Threading is
// pure passthrough — every recursion site forwards the same map.
//
import type { z } from 'zod'
import { useMemo, useState } from 'preact/hooks'

import type { FieldNode } from './schema-to-fields'
import { schemaToFields } from './schema-to-fields'
import { useDebounced } from '../hooks/use-debounced'
import { FieldRenderer } from './field-renderer'
import { ValidationSummary } from './validation-summary'
import { BLOCK_FORM_EFFECTS } from './block-form-effects'

export interface ArrayOverride {
  readonly fixedCount: number
  readonly slotLabel: string
}

export type ArrayOverrides = Readonly<Record<string, ArrayOverride>>

export interface FormProps {
  readonly schema: z.ZodTypeAny
  readonly value: unknown
  readonly onChange: (next: unknown) => void
  readonly idPrefix?: string
  readonly arrayOverrides?: ArrayOverrides
  // Registered Block ID (e.g. `'commentary'`). Threaded down so
  // `<ImageUrlField>` can resolve a per-Block resize target via
  // `getImageTarget('${blockId}.${path}')` (P-FS-04 / D-121). Distinct
  // from `idPrefix`, which is per-instance (`block-${instanceId}-...`).
  readonly blockId?: string
}

export function Form({
  schema,
  value,
  onChange,
  idPrefix = 'field',
  arrayOverrides,
  blockId,
}: FormProps) {
  const root = useMemo(() => schemaToFields(schema, ''), [schema])
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set())
  const debouncedValue = useDebounced(value, COMPUTE_ERRORS_DEBOUNCE_MS)

  const errors = useMemo(
    () => (touched.size === 0 ? EMPTY_ERRORS : computeErrors(schema, debouncedValue)),
    [schema, debouncedValue, touched],
  )

  const markTouched = (path: string) => {
    setTouched((prev) => {
      if (prev.has(path)) return prev
      const next = new Set(prev)
      next.add(path)
      return next
    })
  }

  const rootChildren = unwrapRootChildren(root)
  if (rootChildren === null) {
    return <UnsupportedRoot node={root} />
  }

  // P-037.24 D-132: per-Block side-effect (invisible) — handles
  // derived-field flows like the Big 3 Canvas-composite pipeline.
  const Effect = blockId !== undefined ? BLOCK_FORM_EFFECTS[blockId] : undefined

  return (
    <div class="flex flex-col gap-4">
      <ValidationSummary errors={errors} root={root} idPrefix={idPrefix} />
      {rootChildren.map((child) => (
        <FieldRenderer
          key={child.name}
          node={child}
          value={readField(value, child.name)}
          path={child.name}
          idPrefix={idPrefix}
          errors={errors}
          touched={touched}
          markTouched={markTouched}
          onChange={(next) => onChange(writeField(value, child.name, next))}
          {...(arrayOverrides !== undefined ? { arrayOverrides } : {})}
          {...(blockId !== undefined ? { blockId } : {})}
        />
      ))}
      {Effect !== undefined && <Effect value={value} onChange={onChange} />}
    </div>
  )
}

function unwrapRootChildren(root: FieldNode): readonly FieldNode[] | null {
  if (root.kind === 'object') return root.children
  if (root.kind === 'union') {
    const last = root.variants[root.variants.length - 1]
    if (last !== undefined && last.kind === 'object') return last.children
  }
  return null
}

function UnsupportedRoot({ node }: { node: FieldNode }) {
  return (
    <div class="rounded border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-600">
      <div class="font-medium text-gray-800">Schema not yet supported</div>
      <div class="mt-1 text-xs">
        Top-level <code class="font-mono">{node.kind}</code> root rendering is not part of this build.
      </div>
    </div>
  )
}

function readField(value: unknown, name: string): unknown {
  if (typeof value !== 'object' || value === null) return undefined
  return (value as Record<string, unknown>)[name]
}

function writeField(value: unknown, name: string, next: unknown): Record<string, unknown> {
  const base =
    typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}
  return { ...base, [name]: next }
}

function computeErrors(
  schema: z.ZodTypeAny,
  value: unknown
): Readonly<Record<string, string>> {
  const result = schema.safeParse(value)
  if (result.success) return EMPTY_ERRORS
  const errs: Record<string, string> = {}
  for (const issue of flattenIssues(result.error)) {
    const path = issue.path.join('.')
    if (!(path in errs)) errs[path] = issue.message
  }
  return errs
}

// `ZodInvalidUnion` issues land at the union's path with per-arm errors
// nested in `unionErrors`. Recurse into the LAST arm's errors so per-
// field issues from the rendered "fat" variant (D-033 paired-optional
// CTA UX) surface at their natural paths in the `errors` table. Non-
// union issues pass through unchanged.
function flattenIssues(error: z.ZodError): z.ZodIssue[] {
  const out: z.ZodIssue[] = []
  for (const issue of error.errors) {
    if (issue.code === 'invalid_union') {
      // Zod guarantees ≥1 arm for invalid_union per its source contract;
      // the last arm is the rendered "fat" variant per D-033 UX.
      const arms = issue.unionErrors
      const lastArm = arms[arms.length - 1]!
      out.push(...flattenIssues(lastArm))
    } else {
      out.push(issue)
    }
  }
  return out
}

const EMPTY_ERRORS: Readonly<Record<string, string>> = Object.freeze({})

// Trailing debounce for `safeParse` — keeps validation below the
// keystroke-perception threshold while shielding the schema walk +
// ValidationSummary aggregation from per-keystroke re-runs.
const COMPUTE_ERRORS_DEBOUNCE_MS = 100
